import { ArrowUp, ArrowDown, Trash2, Plus, GripVertical, AlertTriangle, CornerDownRight } from "lucide-react";

export const stageName = (stages, id) => {
  const i = stages.findIndex((s) => s.id === id);
  return i < 0 ? null : `Stap ${i + 1} — ${stages[i].label}`;
};

// Structural route check: broken/empty targets, self-loops, stages no answer leads to.
export function checkRoutes(script) {
  const stages = script.stages || [];
  const ids = new Set(stages.map((s) => s.id));
  const issues = [];
  const reached = new Set();
  stages.forEach((st, si) => (st.branches || []).forEach((b, bi) => {
    const where = `Stap ${si + 1} (${st.label}) → antwoord ${bi + 1} "${b.label || "zonder tekst"}"`;
    if (!b.label?.trim()) issues.push({ level: "error", stage: st.id, msg: `${where}: antwoordtekst is leeg` });
    if (!b.target) issues.push({ level: "error", stage: st.id, msg: `${where}: geen vervolgstap gekozen` });
    else if (!ids.has(b.target)) issues.push({ level: "error", stage: st.id, msg: `${where}: vervolgstap bestaat niet (meer)` });
    else if (b.target === st.id) issues.push({ level: "warn", stage: st.id, msg: `${where}: verwijst naar dezelfde stap` });
    if (b.target) reached.add(b.target);
  }));
  stages.slice(1).forEach((st, i) => {
    if (!reached.has(st.id)) issues.push({ level: "info", stage: st.id, msg: `Stap ${i + 2} (${st.label}) wordt door geen enkel antwoord bereikt (alleen via 'Volgende stap')` });
  });
  return issues;
}

function BranchRow({ b, bi, count, stages, selfId, onChange, onMove, onRemove, testid }) {
  const broken = b.target && !stages.some((s) => s.id === b.target);
  return (
    <div className="grid grid-cols-[auto_minmax(0,1fr)] md:grid-cols-[auto_minmax(0,1fr)_minmax(0,16rem)_auto] items-center gap-2" data-testid={testid}>
      <CornerDownRight className="h-4 w-4 text-[#8994A3] shrink-0" />
      <input data-testid={`${testid}-label`} className="fe-input py-2 text-sm" value={b.label} placeholder="Reactie van de klant, bijv. 'Ja, komt gelegen'"
        onChange={(e) => onChange({ ...b, label: e.target.value })} />
      <select data-testid={`${testid}-target`} value={broken ? "__broken" : b.target || ""} onChange={(e) => onChange({ ...b, target: e.target.value })}
        className={`fe-input py-2 text-sm col-start-2 md:col-start-auto ${!b.target || broken ? "border-[#B0413E] text-[#B0413E]" : ""}`}>
        <option value="">— Kies vervolgstap —</option>
        {broken && <option value="__broken" disabled>Ontbrekende stap ({b.target})</option>}
        {stages.map((s, i) => <option key={s.id} value={s.id}>{`Stap ${i + 1} — ${s.label}${s.id === selfId ? " (deze stap)" : ""}`}</option>)}
      </select>
      <div className="flex items-center col-start-2 md:col-start-auto">
      <button data-testid={`${testid}-up`} disabled={bi === 0} onClick={() => onMove(-1)} className="p-1.5 rounded-lg text-[#5B6675] hover:bg-[#EDF1F5] disabled:opacity-30" aria-label="Omhoog"><ArrowUp className="h-4 w-4" /></button>
      <button data-testid={`${testid}-down`} disabled={bi === count - 1} onClick={() => onMove(1)} className="p-1.5 rounded-lg text-[#5B6675] hover:bg-[#EDF1F5] disabled:opacity-30" aria-label="Omlaag"><ArrowDown className="h-4 w-4" /></button>
      <button data-testid={`${testid}-delete`} onClick={onRemove} className="p-1.5 rounded-lg text-[#B0413E] hover:bg-[#F3DEDD]" aria-label="Antwoord verwijderen"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

export function BranchEditor({ stage, stages, onChange }) {
  const branches = stage.branches || [];
  const set = (list) => onChange(list);
  const move = (i, d) => { const a = [...branches]; [a[i], a[i + d]] = [a[i + d], a[i]]; set(a); };
  const remove = (i) => { if (window.confirm(`Antwoord "${branches[i].label || "zonder tekst"}" verwijderen?`)) set(branches.filter((_, j) => j !== i)); };
  return (
    <div className="mt-4 rounded-xl bg-[#F6F9FB] border border-[#E2E7EC] p-4 space-y-2" data-testid={`branches-${stage.id}`}>
      <p className="text-sm font-medium text-[#16202B]">Reacties van de klant → vervolgstap</p>
      <p className="text-xs text-[#5B6675] mb-1">Tijdens het gesprek klikt de medewerker op de reactie en gaat het script naar de gekozen stap.</p>
      {branches.length === 0 && <p className="text-xs text-[#8994A3]">Nog geen antwoorden — de medewerker gaat dan verder met 'Volgende stap'.</p>}
      {branches.map((b, i) => (
        <BranchRow key={i} b={b} bi={i} count={branches.length} stages={stages} selfId={stage.id} testid={`branch-edit-${stage.id}-${i}`}
          onChange={(nb) => set(branches.map((x, j) => (j === i ? nb : x)))} onMove={(d) => move(i, d)} onRemove={() => remove(i)} />
      ))}
      <button data-testid={`add-branch-${stage.id}`} onClick={() => set([...branches, { label: "", target: "" }])}
        className="inline-flex items-center gap-1 text-xs font-semibold text-[#001C51] px-3 py-1.5 rounded-lg border border-dashed border-[#001C51]/40 hover:bg-[#E3E9F5]">
        <Plus className="h-3.5 w-3.5" /> Antwoord toevoegen
      </button>
    </div>
  );
}

export function StageIssues({ issues }) {
  if (!issues.length) return null;
  return (
    <div className="mt-3 space-y-1" >
      {issues.map((x, i) => (
        <p key={i} className={`text-xs flex items-start gap-1.5 ${x.level === "error" ? "text-[#B0413E]" : x.level === "warn" ? "text-[#B7791F]" : "text-[#5B6675]"}`}>
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {x.msg}
        </p>
      ))}
    </div>
  );
}

export function StageCard({ st, si, stages, issues, setStage, setLines, onDelete, onDragStart, onDrop }) {
  return (
    <div className="rounded-2xl border border-[#E2E7EC] bg-white p-5" data-testid={`edit-stage-${st.id}`} onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
      <div className="flex items-center gap-2 mb-3">
        <span draggable onDragStart={onDragStart} className="cursor-grab active:cursor-grabbing text-[#8994A3] p-1" title="Sleep om te herordenen"><GripVertical className="h-5 w-5" /></span>
        <span className="text-xs font-semibold text-[#001C51] bg-[#E3E9F5] rounded-full px-2.5 py-1">Stap {si + 1}</span>
        <span className="text-[11px] text-[#8994A3]">id: {st.id}</span>
        <button data-testid={`delete-stage-${st.id}`} onClick={onDelete} className="ml-auto p-1.5 rounded-lg text-[#B0413E] hover:bg-[#F3DEDD]"><Trash2 className="h-4 w-4" /></button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 mb-3">
        <label className="block"><span className="text-sm text-[#5B6675] mb-1.5 block">Stap-naam</span>
          <input data-testid={`stage-label-${st.id}`} className="fe-input" value={st.label} onChange={(e) => setStage("label", e.target.value)} /></label>
        <label className="block"><span className="text-sm text-[#5B6675] mb-1.5 block">Doel van deze stap</span>
          <input className="fe-input" value={st.goal || ""} onChange={(e) => setStage("goal", e.target.value)} /></label>
      </div>
      <label className="block"><span className="text-sm text-[#5B6675] mb-1.5 block">Zinnen (één per regel)</span>
        <textarea data-testid={`stage-lines-${st.id}`} className="fe-input resize-y font-medium leading-relaxed" rows={Math.max(3, (st.lines || []).length + 1)}
          value={(st.lines || []).join("\n")} onChange={(e) => setLines(e.target.value)} /></label>
      <BranchEditor stage={st} stages={stages} onChange={(list) => setStage("branches", list)} />
      <StageIssues issues={issues} />
    </div>
  );
}
